<template>
  <!-- Wrap in the app chrome (nav + sidebar) so the prospect selector stays "in the app"
       (Danny, 7.19 #proj-web-app). The prospect route is top-level (not nested under
       DashboardLayout like the other Beta selectors) because the classic ProspectSearch
       self-wraps in ProspectSearchLayout; Beta must self-wrap the same way. prospect-search
       ="false" hides the classic filter panel, leaving just chrome + slot (same as SearchList). -->
  <prospect-search-layout :mini="false" :prospect-search="false">
  <div class="bz-selector-page">
    <div class="bz-pagehead">
      <div class="bz-pagehead-l">
        <h1>Build a Prospect List</h1>
        <beta-pill product="prospect_list" />
      </div>
    </div>

    <div class="bzB rail">
      <div class="bzB-steps">
        <step-card :n="1" title="Industry" icon="factory" :is-open="open === 1" @toggle="toggle(1)"
          :value="industry && industry.name + ' [' + industry.code + ']'">
          <industry-step product="IFP" :selected="industry" :favorites="favorites"
            @select="onIndustrySelect" @toggle-fav="toggleFav" />
        </step-card>

        <step-card :n="2" title="Market Area" icon="public" :is-open="open === 2" @toggle="toggle(2)"
          :value="area && area.label">
          <area-step product="IFP" no-availability :industry="industry" :value="area"
            @change="onAreaChange" />
        </step-card>

        <step-card :n="3" title="Refine list" icon="filter_alt" :is-open="open === 3" @toggle="toggle(3)"
          :value="industry || area ? (nActive ? nActive + ' filter' + (nActive === 1 ? '' : 's') + ' active' : 'No filters — all prospects') : null">
          <refine-step :f="filters" @change="onFilterChange" />
        </step-card>
      </div>

      <div class="bz-rail">
        <div class="bz-rail-h">Your list</div>
        <div class="bz-rail-list">
          <button class="bz-rail-row" :class="{ filled: !!industry }" type="button" @click="toggle(1)">
            <span class="bz-rail-k">Industry</span>
            <span class="bz-rail-v">{{ industry ? industry.name + ' [' + industry.code + ']' : 'Not selected' }}</span>
            <i class="material-icons" style="font-size:14px;line-height:1;color:var(--bzc-faint);">edit</i>
          </button>
          <button class="bz-rail-row" :class="{ filled: !!area }" type="button" @click="toggle(2)">
            <span class="bz-rail-k">Area</span>
            <span class="bz-rail-v">{{ area ? area.label : 'Not selected' }}</span>
            <i class="material-icons" style="font-size:14px;line-height:1;color:var(--bzc-faint);">edit</i>
          </button>
          <button class="bz-rail-row" :class="{ filled: nActive > 0 }" type="button" @click="toggle(3)">
            <span class="bz-rail-k">Filters</span>
            <span class="bz-rail-v">{{ nActive ? nActive + ' active' : 'None' }}</span>
            <i class="material-icons" style="font-size:14px;line-height:1;color:var(--bzc-faint);">edit</i>
          </button>
        </div>

        <div class="bz-hero" :class="{ zero: !counting && count === 0, warn: !counting && count !== null && count > 0 && count < 250, loading: counting, empty: !counting && count === null }">
          <span class="bz-hero-k">Matching prospects</span>
          <span class="bz-hero-n">
            <span v-if="counting" class="bz-hero-spin" role="status" aria-label="Counting"></span>
            <template v-else>{{ heroText }}</template>
          </span>
          <span class="bz-hero-sub">{{ heroSub }}</span>
        </div>

        <div class="bz-rail-foot">
          <div class="bz-status" :class="status.cls">
            <i class="material-icons" style="font-size:16px;line-height:1;">{{ status.icon }}</i>
            <span v-html="status.text"></span>
          </div>
          <div class="bz-cta-wrap">
            <button class="bz-btn primary" type="button" :disabled="!ready || ctaDone || submitting" @click="accessList">
              <template v-if="ctaDone">
                <i class="material-icons" style="font-size:16px;line-height:1;">check</i>Opening your list…
              </template>
              <template v-else-if="submitting">Processing…</template>
              <template v-else>
                Access List<i class="material-icons" style="font-size:16px;line-height:1;">arrow_forward</i>
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  </prospect-search-layout>
</template>

<script>
// Prospect List Beta — live matching count via the classic count endpoint
// (POST /user/search_lists/company, countOnly:1), debounced on every change.
// Access List = the materialize pass (no countOnly) → /search-list/{id}/{count}.
import ProspectSearchLayout from '@/pages/Layout/ProspectSearchLayout.vue';
import StepCard from './StepCard.vue';
import BetaPill from './BetaPill.vue';
import IndustryStep from './IndustryStep.vue';
import AreaStep from './AreaStep.vue';
import RefineStep, { DEFAULT_FILTERS, REV_IDX, VAL_IDX } from './RefineStep.vue';
import { prospectCount, prospectMaxCount, prospectMaterialize, areaCodeOf } from './model';
import {
  trackSelectorViewed, trackSelectorCompleted, trackIndustrySelected, trackStepCompleted,
  trackProspectFilterApplied, trackProspectResultsUpdated, trackProspectNarrowWarning,
} from './analytics';
import './beta-selector.css';

// band index → dollar bounds for the count endpoint's from/to params
// Employee count is sent as literal headcount ("low-high"), aligned positionally with
// EMP_BANDS in RefineStep. Revenue/value are NOT dollars — they're backend band indexes
// (REV_IDX/VAL_IDX, imported above).
const EMP_FROM = [1, 5, 10, 20, 50, 100, 250];
const EMP_TO = [4, 9, 19, 49, 99, 249, null];
const LIMIT_RETRY_MS = 60000; // after a daily-limit refusal, wait this long before counting again

export default {
  name: 'prospect-page',
  components: { ProspectSearchLayout, StepCard, BetaPill, IndustryStep, AreaStep, RefineStep },
  data() {
    return {
      industry: null,
      area: null,
      filters: { ...DEFAULT_FILTERS },
      favorites: [],
      open: 1,
      count: null,
      maxCount: null, // "max available" baseline (industry + area, no refine filters) — 7.18 #6b
      countId: null,
      counting: false,
      limitReached: false, // free-account daily cap hit (API: daily_limit_reached); the global dialog explains it
      limitAt: 0, // when it was last hit (ms); a filter change 60 s+ later tries counting again
      countTimer: null,
      lastFilter: null,
      ctaDone: false,
      submitting: false,
    };
  },
  computed: {
    nActive() {
      const f = this.filters;
      let n = 0;
      if (f.revFrom !== null || f.revTo !== null) n++;
      if (f.valFrom !== null || f.valTo !== null) n++;
      if (f.empFrom !== null || f.empTo !== null) n++;
      if (f.years !== 'any') n++;
      if (f.phone) n++;
      if (f.franchise !== 'any') n++;
      if (f.web !== 'any') n++;
      if (f.social !== 'any') n++;
      return n;
    },
    ready() {
      // 7.18 #6a drops the industry-*ordering* requirement (area/filters are usable
      // first), but the count itself stays industry-anchored: a no-industry All-US
      // request is a full 17.7M-row igdbDA COUNT (slow + meaningless "all companies").
      // Area is optional here — absent area = All US for the chosen industry.
      return !!(this.industry && this.count !== null && this.count > 0);
    },
    heroText() {
      // The template shows a spinner while counting; this only renders the settled value.
      if (this.count === null) return '—';
      return this.count.toLocaleString();
    },
    heroSub() {
      // While a (re)count is in flight, show ONLY the loading line — never the stale
      // "of N available" or a settled sub, which caused the panel to show a spinner and a
      // contradictory number/error at once (7.19 UI-bleeding fix). A revenue/value filter
      // can make this count slow (LenderSquared sales path), so the loading state matters.
      if (this.counting) return 'Counting matching prospects…';
      // Empty state: the "pick an industry" guidance lives in the status box below — don't
      // repeat it here (that duplicate message was the remaining bleed).
      if (this.count === null) return '';
      if (this.maxCount !== null && this.maxCount > this.count) {
        return 'of ' + this.maxCount.toLocaleString() + ' available before filters';
      }
      return 'Updates live with every change';
    },
    status() {
      if (!this.industry) return { cls: 'todo', icon: 'info', text: 'Pick an industry to see how many prospects match — you can set the area & filters first' };
      // Counting takes priority over any prior count so a stale "No prospects match"/"Ready"
      // never shows next to the loading spinner.
      if (this.counting) return { cls: 'todo', icon: 'hourglass_empty', text: 'Counting matching prospects…' };
      if (this.limitReached) return { cls: 'no', icon: 'report', text: '<b>Daily limit reached</b> — free accounts have a daily limit on list previews' };
      if (this.count === 0) return { cls: 'no', icon: 'report', text: 'No prospects match — loosen a filter or widen the area' };
      if (this.count !== null && this.count < 250) return { cls: 'warn', icon: 'warning_amber', text: '<b>Very narrow list</b> — consider loosening filters or widening the area' };
      if (this.count !== null) return { cls: 'ok', icon: 'check_circle', text: '<b>Ready</b> · list refreshed live as you refined — no waiting' };
      return { cls: 'todo', icon: 'info', text: 'Counting…' };
    },
  },
  created() {
    try {
      this.favorites = JSON.parse(localStorage.getItem(this.favKey()) || '[]');
    } catch (e) {
      this.favorites = [];
    }
  },
  mounted() {
    trackSelectorViewed('prospect_list', 'beta');
  },
  methods: {
    favKey() {
      const uid = this.$store.getters['user/user_id'] || 'anon';
      return 'bzSelectorFavs:' + uid; // shared industry favorites
    },
    toggle(n) {
      this.open = this.open === n ? 0 : n;
    },
    toggleFav(code) {
      const i = this.favorites.indexOf(code);
      if (i === -1) this.favorites.push(code);
      else this.favorites.splice(i, 1);
      localStorage.setItem(this.favKey(), JSON.stringify(this.favorites));
    },
    onIndustrySelect({ node, method }) {
      // Ordering is free now (7.18 #6a) — do NOT wipe an area the user already picked.
      // But DO clear the count/max so the hero shows "…" during the recount instead of
      // the previous selection's stale number (and status doesn't read "Ready" on stale data).
      this.industry = node;
      this.ctaDone = false;
      this.count = null;
      this.maxCount = null;
      this.open = this.area ? 3 : 2;
      trackIndustrySelected('prospect_list', 'beta', method);
      this.refreshMax();
      this.scheduleCount();
    },
    onAreaChange(area) {
      this.area = area;
      this.count = null;
      this.maxCount = null;
      this.open = 3;
      this.ctaDone = false;
      trackStepCompleted('prospect_list', 'beta', 'market_area');
      this.refreshMax();
      this.scheduleCount();
    },
    onFilterChange({ patch, filter }) {
      this.filters = { ...this.filters, ...patch };
      this.lastFilter = filter;
      this.ctaDone = false;
      // Filters don't change the max-available baseline — only re-run the filtered count.
      this.scheduleCount();
    },
    baseParams() {
      // Industry + area only (no refine filters). Either may be absent now (7.18 #6a):
      // no industry -> all industries (backend caps at 10k); no area -> All US (the
      // backend's All-US guard, fixed 7.18 #6c, treats an absent area as 9999).
      const p = { zips: '0' };
      if (this.industry) p.industry = this.industry.code;
      const a = areaCodeOf(this.area);
      if (a !== null && a !== undefined) p.area = a;
      return p;
    },
    countParams() {
      const f = this.filters;
      const p = this.baseParams();
      // Revenue/value: send the backend's BAND INDEX (see REV_IDX/VAL_IDX). Sending dollars
      // made the backend look up a nonexistent array key -> to_value 0 -> "sales > x AND < 0"
      // -> always 0 results; and the old open-ended top band sent no sales_to at all, so the
      // filter was skipped entirely (which is why only "$10m+" appeared to work). Index 1 is
      // the backend's "any" on either side, and both sides must be set for it to filter.
      if (f.revFrom !== null || f.revTo !== null) {
        p.sales_from = f.revFrom !== null ? REV_IDX[f.revFrom] : 1;
        p.sales_to = f.revTo !== null ? REV_IDX[f.revTo] : 1;
      }
      if (f.valFrom !== null || f.valTo !== null) {
        p.value_from = f.valFrom !== null ? VAL_IDX[f.valFrom] : 1;
        p.value_to = f.valTo !== null ? VAL_IDX[f.valTo] : 1;
      }
      // Employee count is a literal "low-high" of headcount (backend compares locemp), so raw
      // numbers are right here — but the open-ended top band must send a real upper bound,
      // otherwise `locemp <= ''` matches nothing.
      if (f.empFrom !== null || f.empTo !== null) {
        const from = f.empFrom !== null ? EMP_FROM[f.empFrom] : 0;
        const to = f.empTo !== null && EMP_TO[f.empTo] !== null ? EMP_TO[f.empTo] : 1000000;
        p.employee_count = from + '-' + to;
      }
      // Years is "minYears-maxYears" (backend converts to a founded-year window). Sending
      // "5-" left the max empty -> yey2k >= currentYear AND <= currentYear-5 -> always 0.
      if (f.years !== 'any') p.years_in_business = f.years + '-200';
      if (f.phone) p.contact_phone = 1;
      if (f.franchise !== 'any') p.franchise = f.franchise === 'only' ? 1 : 0;
      if (f.web === 'has') p.web_presence = 1;
      if (f.social === 'active') p.social_presence = 1;
      return p;
    },
    scheduleCount() {
      if (!this.industry) return; // count is industry-anchored (7.18 #6a) — area optional
      clearTimeout(this.countTimer);
      // Capped for the day: further counts would be refused (and re-open the dialog on each filter
      // tweak), so stop asking and keep the "Daily limit reached" status. After 60 s a filter change
      // tries again, in case the cap has lifted.
      if (this.limitActive()) { this.counting = false; return; }
      this.limitReached = false;
      this.counting = true;
      this.countTimer = setTimeout(this.runCount, 400);
    },
    limitActive() {
      return this.limitReached && Date.now() - this.limitAt < LIMIT_RETRY_MS;
    },
    markLimit() {
      this.limitReached = true;
      this.limitAt = Date.now();
    },
    async refreshMax() {
      // "Max available" = current industry + area with no refine filters (7.18 #6b).
      // Recomputed only when industry/area change (cheap indexed count), not per filter.
      if (!this.industry || this.limitActive()) { this.maxCount = null; return; }
      try {
        const res = await prospectMaxCount(this.baseParams());
        this.maxCount = typeof res.count === 'number' ? res.count : parseInt(res.count, 10) || 0;
      } catch (e) {
        if (e && e.dailyLimit) this.markLimit();
        if (!(e && String(e.message).indexOf('superseded') !== -1)) this.maxCount = null;
      }
    },
    async runCount() {
      try {
        const res = await prospectCount(this.countParams());
        this.limitReached = false;
        this.count = typeof res.count === 'number' ? res.count : parseInt(res.count, 10) || 0;
        this.countId = res.id;
        trackProspectResultsUpdated(this.count, this.nActive);
        if (this.lastFilter) trackProspectFilterApplied(this.lastFilter, 'changed', this.count);
        if (this.count < 250) trackProspectNarrowWarning(this.count, this.lastFilter);
        this.lastFilter = null;
      } catch (e) {
        if (e && e.dailyLimit) this.markLimit();
        if (!(e && String(e.message).indexOf('superseded') !== -1)) this.count = null;
      } finally {
        this.counting = false;
      }
    },
    async accessList() {
      if (!this.ready || this.submitting || this.ctaDone) return;
      this.submitting = true;
      try {
        // materialize pass (persists the list server-side), then open it
        const res = await prospectMaterialize(this.countParams());
        trackSelectorCompleted('prospect_list', 'beta');
        this.ctaDone = true;
        this.$router.push('/search-list/' + res.id + '/' + (res.count || this.count));
      } catch (e) {
        this.ctaDone = false;
        // Daily cap: the global dialog already explained it; don't add a contradictory generic alert.
        if (e && e.dailyLimit) return;
        this.$store.dispatch('alerts/error', 'Something went wrong accessing this list. Please try again.');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
